import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {connect} from 'pwa-helpers';
import store from '../../store';
import {
	selectOlivioBracket,
	selectOlivioBracketIsSatisfied,
	selectOlivioCandelabraTop,
	selectOlivioCandelabraTopIsSatisfied,
	selectOlivioDesign,
	selectOlivioDesignIsCandelabra,
	selectOlivioDesignIsFloracion,
	selectOlivioDesignIsSistema,
	selectOlivioHousingColourInlay,
	selectOlivioHousingColourInlayIsSatisfied,
	selectOlivioHousingColourPole,
	selectOlivioHousingColourPoleIsSatisfied,
	selectOlivioPoleDiameter,
	selectOlivioPoleDiameterIsSatisfied,
	selectOlivioNumberOfHeads,
	selectOlivioNumberOfHeadsIsSatisfied,
	selectOlivioPoleHeight,
	selectOlivioPoleHeightIsSatisfied,
	selectOlivioPoleMaterial,
	selectOlivioPoleMaterialIsSatisfied,
	selectOlivioWoodType,
	selectOlivioWoodTypeIsSatisfied,
	selectOlivioDesignIsUniversal,
	selectOlivioBase,
	selectOlivioBaseIsSatisfied,
	selectOlivioSeparateBuriedBase,
	selectOlivioSeparateBuriedBaseIsSatisfied,
	selectOlivioMaxNumberOfHeads,
	selectOlivioSecondDoor,
	selectOlivioSecondDoorIsSatisfied,
	selectOlivioBuriedBaseSize,
	selectOlivioBuriedBaseSizeIsSatisfied
} from '../olivioSelectors';
import {
	olivioBaseOptions,
	olivioBollardPoleHeightOptions,
	olivioBracketOptions,
	olivioBuriedBaseSizeOptions,
	olivioCandelabraPoleHeightOptions,
	olivioCandelabraTopOptions,
	olivioFloracionPoleDiameterOptions,
	olivioFloracionPoleHeightOptions,
	olivioFloracionWoodCylindricalPoleHeightOptions,
	olivioNumberOfHeadsOptions,
	olivioPoleMaterialOptions,
	olivioSecondDoorOptions,
	olivioSeparateBuriedBaseOptions,
	olivioSistemaPoleHeightOptions,
	olivioWoodTypeOptions
} from '../olivioModel';
import {spcValueSelectorFactory} from '../../selectors';

@customElement('olivio-general-design-settings')
export class OlivioGeneralDesignSettings extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				display: flex;
				break-inside: avoid;
				flex-flow: column;
				place-content: flex-start;
				gap: var(--small-padding);
			}

			.box {
				background-color: var(--color-widget-background);
				padding: 12px;
				display: flex;
				break-inside: avoid;
				flex-flow: column;
				place-content: flex-start;
				gap: var(--large-space);
			}
		`
	];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.lang = state.status.lang;
		this.design = selectOlivioDesign(state);
		this.poleMaterial = selectOlivioPoleMaterial(state);
		this.poleHeightOptions = [];
		switch (this.design) {
			case 'floracion':
				this.poleHeightOptions = this.poleMaterial === 'wood_cylindrical' ? olivioFloracionWoodCylindricalPoleHeightOptions : olivioFloracionPoleHeightOptions;
				break;
			case 'sistema':
				this.poleHeightOptions = olivioSistemaPoleHeightOptions;
				break;
			case 'candelabra':
				this.poleHeightOptions = olivioCandelabraPoleHeightOptions;
				break;
			case 'bollard':
				this.poleHeightOptions = olivioBollardPoleHeightOptions;
				break;
		}

	}

	housingSizeCanBeSelected(state, id) {
		return (id !== 'custom')
			&&
			!(selectOlivioDesignIsCandelabra(state) && id === 'OlivioPiccolo'); // no piccolo for candelabra
	}
	numberOfHeadsCanBeSelected(state, id) {
		const max = selectOlivioMaxNumberOfHeads(state);
		return (parseInt(id, 10) <= max);
	}
	poleDiameterCanBeSelected(state, id) {
		const poleHeight = selectOlivioPoleHeight(state);
		return (id === '220') ||
			(id === '168' && poleHeight < 7) ||
			(id === '140' && poleHeight < 6);
	}
	baseCanBeSelected(state, id) {
		const poleHeight = selectOlivioPoleHeight(state);
		const design = selectOlivioDesign(state);
		return (id === 'buriedbase') ||
			(id === 'FlangePlateDE') ||
			(id === 'FlangePlateFRsmall' && poleHeight <= 7 && design !== 'floracion') ||
			(id === 'FlangePlateFR' && (poleHeight > 7 || design === 'floracion'));
	}
	separateBaseCanBeSelected(state, id) {
		const poleHeight = selectOlivioPoleHeight(state);
		const design = selectOlivioDesign(state);
		const base = selectOlivioBase(state);
		return (id === 'none') ||
			(id === 'separateBuriedBase' && base === 'FlangePlateDE');
	}
	secondDoorCanBeSelected(state, id) {
		const design = selectOlivioDesign(state);
		return (id === 'none') ||
			(id === 'secondDoor' && design !== 'candelabra');
	}
	buriedBaseSizeCanBeSelected(state, id) {
		const base = selectOlivioBase(state);
		return (base === 'buriedbase');
	}
	render() {
		return html`
			${when(!selectOlivioDesignIsUniversal(this.state), () => html`
				<div class="box">
					<list-select
						.title=${{
							de: 'Masthöhe',
							en: 'Pole Height',
							fr: 'hauteur du mât'
						}}
						.options=${this.poleHeightOptions}
						action='olivio/setPoleHeight'
						.selected=${selectOlivioPoleHeight}
						.canBeSelected=${() => true}
						.isSatisfied=${selectOlivioPoleHeightIsSatisfied}>
					</list-select>
					${when(selectOlivioDesignIsSistema(this.state) || selectOlivioDesignIsFloracion(this.state), () => html`
						<list-select
							.title=${{
								de: 'Anzahl Leuchtenköpfe',
								en: 'Number of Luminairs',
								fr: 'nombre de luminaires'
							}}
							.options=${olivioNumberOfHeadsOptions}
							action='olivio/setNumberOfHeads'
							.selected=${selectOlivioNumberOfHeads}
							.canBeSelected=${this.numberOfHeadsCanBeSelected}
							.isSatisfied=${selectOlivioNumberOfHeadsIsSatisfied}>
						</list-select>
					`)}
					${when(selectOlivioDesignIsCandelabra(this.state), () => html`
						<list-select
							.title=${{de: 'Form', en: 'Form', fr: 'Form'}}
							.options=${olivioCandelabraTopOptions}
							action='olivio/setOlivioCandelabraTop'
							.selected=${selectOlivioCandelabraTop}
							.canBeSelected=${() => true}
							.isSatisfied=${selectOlivioCandelabraTopIsSatisfied}>
						</list-select>
					`)}
				</div>
			`)}

			${when(selectOlivioDesignIsSistema(this.state) || selectOlivioDesignIsFloracion(this.state), () => html`
				<div class="box">
					<olivio-layout-settings></olivio-layout-settings>
				</div>
			`)}

			<div class="box">
				${when(selectOlivioDesignIsSistema(this.state), () => html`
					<list-select
						.title=${{de: 'Ausleger', en: 'Bracket', fr: 'console'}}
						.options=${olivioBracketOptions}
						action='olivio/setBracket'
						.selected=${selectOlivioBracket}
						.canBeSelected=${() => true}
						.isSatisfied=${selectOlivioBracketIsSatisfied}>
					</list-select>
				`)}
				${when(selectOlivioDesignIsFloracion(this.state), () => html`
					<list-select
						.title=${{de: 'Mastmaterial', en: 'Pole Material', fr: 'matériau du mât'}}
						.options=${olivioPoleMaterialOptions}
						action='olivio/setPoleMaterial'
						.selected=${selectOlivioPoleMaterial}
						.canBeSelected=${() => true}
						.isSatisfied=${selectOlivioPoleMaterialIsSatisfied}>
					</list-select>
				`)}

				<spc-attribute .attributeId=${{localId: 'HS_HousingColour'}} .hideDisabled=${true}></spc-attribute>

				${when(selectOlivioDesignIsFloracion(this.state), () => html`
					${when(selectOlivioPoleMaterial(this.state) === 'steel', () => html`
						<color-select
							.title=${{de: 'Gehäusefarbe Inlay', en: 'housing colour inlay', fr: 'finition inlay'}}
							.options=${spcValueSelectorFactory('HS_HousingColour')(this.state)}
							action='olivio/setHousingColourInlay'
							.selected=${selectOlivioHousingColourInlay}
							.canBeSelected=${() => true}
							.isSatisfied=${selectOlivioHousingColourInlayIsSatisfied}>
						</color-select>
						<color-select
							.title=${{de: 'Gehäusefarbe Mast', en: 'housing colour pole', fr: 'finition pole'}}
							.options=${spcValueSelectorFactory('HS_HousingColour')(this.state)}
							action='olivio/setHousingColourPole'
							.selected=${selectOlivioHousingColourPole}
							.canBeSelected=${() => true}
							.isSatisfied=${selectOlivioHousingColourPoleIsSatisfied}>
						</color-select>
					`)}
					${when(selectOlivioPoleMaterial(this.state) !== 'steel', () => html`
						<list-select
							.title=${{de: 'Holzart', en: 'Wood Type', fr: 'type de bois'}}
							.options=${olivioWoodTypeOptions}
							action='olivio/setWoodType'
							.selected=${selectOlivioWoodType}
							.canBeSelected=${() => true}
							.isSatisfied=${selectOlivioWoodTypeIsSatisfied}>
						</list-select>
					`)}
					${when(selectOlivioPoleMaterial(this.state) === 'wood_cylindrical', () => html`
						<list-select
							.title=${{de: 'Mast Durchmesser', en: 'Pole Diameter', fr: 'diamètre du mât'}}
							.options=${olivioFloracionPoleDiameterOptions}
							action='olivio/setPoleDiameter'
							.selected=${selectOlivioPoleDiameter}
							.canBeSelected=${this.poleDiameterCanBeSelected}
							.isSatisfied=${selectOlivioPoleDiameterIsSatisfied}>
						</list-select>
					`)}
				`)}

				${when(selectOlivioDesignIsSistema(this.state) || selectOlivioDesignIsFloracion(this.state) || selectOlivioDesignIsCandelabra(this.state), () => html`
					<list-select
						.title=${{de: 'Anbindung / Gründung', en: 'mounting / base', fr: 'mode de fixation'}}
						.options=${olivioBaseOptions}
						action='olivio/setBase'
						.selected=${selectOlivioBase}
						.canBeSelected=${this.baseCanBeSelected}
						.isSatisfied=${selectOlivioBaseIsSatisfied}>
					</list-select>
					<list-select
						.title=${{de: 'Erdstück Länge', en: 'Buried Base Size', fr: 'base à enfouissement longueur'}}
						.options=${olivioBuriedBaseSizeOptions}
						action='olivio/setBuriedBaseSize'
						.selected=${selectOlivioBuriedBaseSize}
						.canBeSelected=${this.buriedBaseSizeCanBeSelected}
						.isSatisfied=${selectOlivioBuriedBaseSizeIsSatisfied}>
					</list-select>
					<list-select
						.title=${{de: 'zusätzliche Gründung', en: 'additional mounting', fr: 'pièce de terre séparée'}}
						.options=${olivioSeparateBuriedBaseOptions}
						action='olivio/setSeparateBuriedBase'
						.selected=${selectOlivioSeparateBuriedBase}
						.canBeSelected=${this.separateBaseCanBeSelected}
						.isSatisfied=${selectOlivioSeparateBuriedBaseIsSatisfied}>
					</list-select>
					<list-select
						.title=${{de: '2. Masttür', en: '2. Pole Door', fr: '2. Porte de mât'}}
						.options=${olivioSecondDoorOptions}
						action='olivio/setSecondDoor'
						.selected=${selectOlivioSecondDoor}
						.canBeSelected=${this.secondDoorCanBeSelected}
						.isSatisfied=${selectOlivioSecondDoorIsSatisfied}>
					</list-select>
				`)}

			</div>

		`;
	}
}
